import { SearchIcon } from "@heroicons/react/outline";
import compact from "lodash/compact";
import { MouseEvent, MouseEventHandler, SVGProps, useState } from "react";
import { IconType } from "react-icons";
import { TbPlus } from "react-icons/tb";
import { useLocation } from "react-router-dom";
import { CreatedArtifactFragmentFragment } from "types/graphql-schema";
import { TFLocationState } from "types/topicflow";

import ArtifactCreationDialog from "@apps/artifact-creation-dialog/artifact-creation-dialog";
import useLabel from "@apps/use-label/use-label";
import {
  currentOrganizationVar,
  currentUserVar,
  isAdminVar,
} from "@cache/cache";
import Link, { useLink } from "@components/link/link";
import Tooltip from "@components/tooltip/tooltip";
import { getTopicflowIcon } from "@components/topicflow-icon/topicflow-icon";
import { classNames } from "@helpers/css";
import { getUrl, toWithBackground } from "@helpers/helpers";

export const sidebarButtonClassName = "px-2 py-1 rounded";

type Props = {
  onClickSearch: () => void;
  menuIsCollapsed: boolean;
};

type SidebarNavItem = {
  id: string;
  name: string;
  href: string;
  groupName: string;
  hideGroupName?: boolean;
  icon: IconType | ((props: SVGProps<SVGSVGElement>) => JSX.Element);
  activeUrls: string[];
};

const getButtonClassName = (isActive = false, menuIsCollapsed: boolean) => {
  return classNames(
    "group flex items-center text-sm font-normal gap-2 text-left",
    sidebarButtonClassName,
    menuIsCollapsed ? "justify-center" : "justify-start flex-1  w-full",
    isActive
      ? "text-blue-600 bg-blue-50 hover:bg-blue-100"
      : "text-gray-800 hover:bg-gray-100"
  );
};

const SidebarNavigationItem = ({
  item,
  menuIsCollapsed = false,
  onClickNavigation,
  isActive,
}: {
  item: SidebarNavItem;
  menuIsCollapsed: boolean;
  onClickNavigation: (
    item: SidebarNavItem
  ) => (e: MouseEvent<HTMLAnchorElement>) => void;
  isActive: boolean;
}) => (
  <div key={item.name}>
    <div className={classNames("flex ")}>
      <Tooltip placement="right" text={menuIsCollapsed ? item.name : ""}>
        <span className="flex-1">
          <Link
            to={item.href}
            className={getButtonClassName(isActive, menuIsCollapsed)}
            onClick={onClickNavigation(item)}
          >
            <item.icon
              className={classNames(
                `shrink-0`,
                "h-4 w-4",
                isActive
                  ? "text-blue-600 group-hover:text-blue-600"
                  : "text-gray-500 group-hover:text-gray-800"
              )}
            />
            {!menuIsCollapsed && item.name}
          </Link>
        </span>
      </Tooltip>
    </div>
  </div>
);

const SidebarNavigation: React.FC<Props> = ({
  menuIsCollapsed,
  onClickSearch,
}: {
  menuIsCollapsed: boolean;
  onClickSearch: () => void;
}) => {
  const label = useLabel();
  const location = useLocation<TFLocationState>();
  const isAdmin = isAdminVar();
  const currentUser = currentUserVar();
  const currentOrganization = currentOrganizationVar();
  const hasReports = currentUser.directReports.edges.length > 0;
  const [showCreationDialog, setShowCreationDialog] = useState(false);

  const navigationItems: SidebarNavItem[] = compact([
    {
      id: "dashboard",
      name: "Home",
      href: "/dashboard",
      activeUrls: ["/dashboard"],
      icon: getTopicflowIcon("home"),
      groupName: "Home",
    },
    currentOrganization.featureFlags.profilePage && {
      id: "profile",
      name: "Profile",
      href: `/profile/${currentUser.id}`,
      activeUrls: [`/profile/${currentUser.id}`],
      icon: getTopicflowIcon("user"),
      groupName: "Home",
    },
    currentOrganization.featureFlags.explorer && {
      id: "explorer",
      name: "Explorer",
      href: "/explorer",
      activeUrls: ["/explorer"],
      icon: getTopicflowIcon("explorer"),
      groupName: "Home",
    },
    currentOrganization.featureFlags.meetings && {
      id: "calendar",
      name: "Meetings",
      href: "/meetings",
      activeUrls: ["/meetings", "/meeting", "/calendar"],
      icon: getTopicflowIcon("meeting"),
      groupName: "expectations",
    },
    currentOrganization.featureFlags.goals && {
      id: "goalOverview",
      name: label("goal", { pluralize: true, capitalize: true }),
      href: "/goals",
      activeUrls: ["/goals", "/kpis", "/goal-alignment"],
      icon: getTopicflowIcon("goal"),
      groupName: "expectations",
    },
    currentOrganization.featureFlags.recognitions && {
      id: "recognitions",
      name: label("recognition", { pluralize: true, capitalize: true }),
      href: "/recognition-board",
      activeUrls: ["/recognition-board"],
      icon: getTopicflowIcon("recognition"),
      groupName: "expectations",
    },
    currentOrganization.featureFlags.feedbacks && {
      id: "feedback",
      name: label("feedback", { capitalize: true }),
      href: "/feedbacks/received",
      activeUrls: ["/feedbacks/received"],
      icon: getTopicflowIcon("feedback"),
      groupName: "expectations",
    },
    {
      id: "assessments",
      name: label("review", { capitalize: true, pluralize: true }),
      href: "/assessments",
      activeUrls: ["/assessments"],
      icon: getTopicflowIcon("assessment"),
      groupName: "development",
    },
    {
      id: "engagement-survey",
      name: "Surveys",
      href: "/survey",
      activeUrls: ["/survey"],
      icon: getTopicflowIcon("survey"),
      groupName: "development",
    },
    {
      id: "programs",
      name: "Programs",
      href: "/programs",
      activeUrls: ["/programs"],
      icon: getTopicflowIcon("program"),
      groupName: "development",
    },
    (isAdmin || hasReports) && {
      id: "reporting",
      name: "Reporting",
      href: "/reporting",
      activeUrls: ["/reporting"],
      icon: getTopicflowIcon("reporting"),
      groupName: "development",
    },
  ]);
  const navigationItemsByGroup = navigationItems.reduce((memo, navItem) => {
    if (!memo[navItem.groupName]) {
      memo[navItem.groupName] = [];
    }
    memo[navItem.groupName] = [...memo[navItem.groupName], navItem];
    return memo;
  }, {} as { [key: string]: SidebarNavItem[] });

  const handleClickNavigationItem =
    (item: SidebarNavItem): MouseEventHandler<HTMLAnchorElement> =>
    (e) => {
      if (item.id === "search") {
        e.preventDefault();
        onClickSearch();
      }
    };

  const link = useLink();
  const handleCloseCreationDialog = (
    artifact?: CreatedArtifactFragmentFragment
  ) => {
    setShowCreationDialog(false);
    if (artifact) {
      link.redirect(
        toWithBackground({
          pathname: getUrl({
            artifactId: artifact.id,
            artifactType: artifact.artifactType,
          }),
          location,
        })
      );
    }
  };

  const searchPlusButtonClassName =
    "text-xs bg-white shadow-sm tracking-tight px-2 py-1 rounded-sm border text-gray-400 flex items-center gap-1 hover:text-gray-600 hover:border-gray-300 hover:bg-black/5";

  return (
    <>
      {showCreationDialog && (
        <ArtifactCreationDialog onClose={handleCloseCreationDialog} />
      )}
      <div
        className={classNames(
          "flex flex-col justify-center items-center ",
          !menuIsCollapsed ? "hidden" : ""
        )}
      >
        <Tooltip text="Search" placement="right">
          <button
            className={classNames(
              menuIsCollapsed
                ? getButtonClassName(false, menuIsCollapsed)
                : searchPlusButtonClassName,
              "mb-2"
            )}
            onClick={onClickSearch}
          >
            <SearchIcon className="h-4 w-4 text-gray-500" />
          </button>
        </Tooltip>
        <Tooltip text="Create" placement="right">
          <button
            onClick={() => setShowCreationDialog(true)}
            className={classNames(
              menuIsCollapsed
                ? getButtonClassName(false, menuIsCollapsed)
                : searchPlusButtonClassName,
              "text-gray-500"
            )}
          >
            <TbPlus className="h-4 w-4 text-gray-500" />
            {!menuIsCollapsed && <span>New</span>}
          </button>
        </Tooltip>
      </div>
      <div>
        {Object.keys(navigationItemsByGroup).map((key, i) => (
          <div key={key}>
            {(menuIsCollapsed || i > 0) && (
              <div
                className={classNames(
                  "h-px bg-gray-200",
                  menuIsCollapsed ? "my-[10px]" : "my-2"
                )}
              />
            )}
            <div
              className={classNames(
                "flex flex-col ",
                menuIsCollapsed ? "justify-center items-center gap-2" : "gap-1"
              )}
            >
              {navigationItemsByGroup[key].map((item) => (
                <SidebarNavigationItem
                  item={item}
                  isActive={item.activeUrls.some((url) =>
                    window.location.href.startsWith(
                      `${window.location.origin}${url}`
                    )
                  )}
                  menuIsCollapsed={menuIsCollapsed}
                  onClickNavigation={handleClickNavigationItem}
                  key={item.name}
                />
              ))}
            </div>
          </div>
        ))}
      </div>
    </>
  );
};

export default SidebarNavigation;
